import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, LinkIcon, ArrowRight } from 'lucide-react';
import { starterRecipeAdminService } from '@/services/starterRecipe/starterRecipeAdminService';
import StarterRecipeEditDialog from './StarterRecipeEditDialog';

interface StarterRecipeImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const StarterRecipeImportDialog: React.FC<StarterRecipeImportDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [url, setUrl] = useState('');
  const [scrapedRecipe, setScrapedRecipe] = useState<any>(null);

  // Scrape recipe mutation
  const scrapeMutation = useMutation({
    mutationFn: (url: string) => starterRecipeAdminService.scrapeRecipeFromUrl(url),
    onSuccess: (data) => {
      console.log('Recipe scraped successfully:', data);
      setScrapedRecipe(data);
      toast.success('Recipe imported! Review and save.');
    },
    onError: (error: Error) => {
      console.error('Scrape error:', error);
      toast.error(`Failed to import recipe: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error('Please enter a URL');
      return;
    }
    scrapeMutation.mutate(url);
  };

  const handleClose = () => {
    setUrl('');
    setScrapedRecipe(null);
    onOpenChange(false);
  };

  const handleEditSuccess = () => {
    setUrl('');
    setScrapedRecipe(null);
    onSuccess();
  };

  // If we have a scraped recipe, show the edit dialog
  if (scrapedRecipe) {
    // Convert scraped recipe to StarterRecipe format
    const starterRecipeData = {
      id: '', // Will be generated on save
      title: scrapedRecipe.title || 'Untitled Recipe',
      description: scrapedRecipe.description || null,
      ingredients: scrapedRecipe.ingredients || [],
      instructions: scrapedRecipe.instructions || [],
      prep_time: scrapedRecipe.prepTime || null,
      cook_time: scrapedRecipe.cookTime || null,
      total_time: scrapedRecipe.totalTime || null,
      servings: scrapedRecipe.servings || null,
      source_url: scrapedRecipe.sourceUrl || url,
      image_url: scrapedRecipe.imageUrl || null,
      storage_image_url: scrapedRecipe.storage_image_url || null,
      storage_image_path: scrapedRecipe.storage_image_path || null,
      tags: scrapedRecipe.tags || [],
      structured_ingredients_metric: scrapedRecipe.structured_ingredients_metric || null,
      structured_ingredients_imperial: scrapedRecipe.structured_ingredients_imperial || null,
      imperial_version: scrapedRecipe.imperial_version || null,
      metric_version: scrapedRecipe.metric_version || null,
      base_measurement_system: scrapedRecipe.baseMeasurementSystem || 'metric',
      is_active: false,
      display_order: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    return (
      <StarterRecipeEditDialog
        recipe={starterRecipeData}
        open={true}
        onOpenChange={(open) => {
          if (!open) {
            setScrapedRecipe(null);
          }
        }}
        onSuccess={handleEditSuccess}
        isNew={true}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Recipe from URL</DialogTitle>
          <DialogDescription>
            Enter a recipe URL to import. The recipe will be scraped and you can 
            edit it before saving.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="url">Recipe URL</Label>
            <div className="relative">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="url"
                type="url"
                placeholder="https://example.com/recipe"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="pl-10"
                disabled={scrapeMutation.isPending}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={scrapeMutation.isPending || !url.trim()}>
              {scrapeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  Import
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StarterRecipeImportDialog;
